import { Close } from "@components/icons"
import VisualLink from "@components/VisualLink/Desktop"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { getIn } from "@utils/index"
import megaMenuConfig from "@utils/megaMenuConfig"
import _ from "lodash-es"
import * as R from "ramda"
import React from "react"
import styled, { css } from "styled-components"

import BrandList from "./BrandList"
import DeviceList from "./DeviceList"
import SeriesList from "./SeriesList"
import VerticalExpandAnimation from "./VerticalExpandAnimation"

const Container = styled.div<{ topOffset?: number }>`
  display: none;
  position: absolute;
  ${({ topOffset }) => `max-height: calc(100vh - ${topOffset || 88}px - 80px);`}
  width: 100vw;
  align-items: flex-start;
  background-color: ${colors.offWhite};
  overflow: hidden;
  overflow-y: auto;
  cursor: default;

  @media ${breakpoints.mediumLaptop} {
    display: flex;
  }
`

const Content = styled.div<{ leftOffset?: boolean }>`
  display: flex;
  padding: 80px calc(1.5 / 12 * 100vw) 40px calc(2.5 / 12 * 100vw);
  flex-direction: row;
  flex-grow: 1;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;

  @media ${breakpoints.mediumLaptop} {
    ${({ leftOffset }) =>
      leftOffset
        ? `padding: 80px calc(1.5 / 12 * 100vw) 40px calc(3.25 / 12 * 100vw);`
        : `padding: 80px calc(1.5 / 12 * 100vw) 40px calc(1.25 / 12 * 100vw);`}
  }
`

const maxWidthByCol = {
  1: "calc(7.80 / 12 * 100vw) - 32px",
  2: "calc(3.90 / 12 * 100vw) - 32px",
  3: "calc(2.60 / 12 * 100vw) - 32px",
  4: "calc(1.95 / 12 * 100vw) - 32px",
  5: "calc(1.55 / 12 * 100vw) - 32px",
}

const maxWidthByColLargeLaptop = {
  1: "calc(8.00 / 12 * 100vw) - 32px",
  2: "calc(3.95 / 12 * 100vw) - 32px",
  3: "calc(2.65 / 12 * 100vw) - 32px",
  4: "calc(2.00 / 12 * 100vw) - 32px",
  5: "calc(1.60 / 12 * 100vw) - 32px",
}

const maxWidthByColDesktop = {
  1: "calc(8.25 / 12 * 100vw) - 32px",
  2: "calc(4.15 / 12 * 100vw) - 32px",
  3: "calc(2.75 / 12 * 100vw) - 32px",
  4: "calc(2.10 / 12 * 100vw) - 32px",
  5: "calc(1.65 / 12 * 100vw) - 32px",
}

const Column = styled.div<{ col?: number }>`
  ${({ col }) => css`
    display: flex;
    width: fit-content;
    min-width: ${maxWidthByCol[col || 3]};
    flex-direction: row;
    flex-shrink: 0;
    flex-wrap: wrap;

    @media ${breakpoints.largeLaptop} {
      min-width: ${maxWidthByColLargeLaptop[col || 3]};
    }

    @media ${breakpoints.desktop} {
      min-width: ${maxWidthByColDesktop[col || 3]};
      padding: 0 12px;
    }
  `}
`

const Brand = styled.div``

const LeftSideMenu = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  padding-top: 92px;
  padding-left: calc(0.75 / 12 * 100vw);
  flex-direction: column;
  align-items: flex-start;
`

const RightSideMenu = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  padding-top: 72px;
  padding-right: calc(0.5 / 12 * 100vw);
  flex-direction: column;
  align-items: flex-end;
`

const CloseButton = styled.button`
  display: flex;
  width: 44px;
  height: 44px;
  padding: 6px;
  transform: background-color ${animation.medium} ${cubicBezier};
  background: ${colors.dividerLineGray};
  border: none;
  border-radius: 2px;
  outline: none;

  &:active {
    outline: none;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    cursor: pointer;
    transform: background-color ${animation.quick} ${cubicBezier};
    background-color: ${colors.lightGray};

    svg {
      stroke: ${colors.offBlack};
    }
  }
`

interface MegaMenuProps {
  activeItem?: string
  data: any
  megaMenuLinks: Array<any>
  onRequestClose?: () => void
  topOffset?: number
}

const MegaMenu: React.FC<MegaMenuProps> = ({
  activeItem,
  data,
  megaMenuLinks,
  onRequestClose,
  topOffset,
}) => {
  const deviceTypeData = activeItem ? data[activeItem] : {}
  const deviceTypeConfig = activeItem ? megaMenuConfig.typeData[activeItem] : {}
  const { slug } = getIn(deviceTypeData, "deviceType", {})

  const activeItemMegaMenu =
    megaMenuLinks.filter((item) => item.slug === slug)[0] || []
  const activeItemMegaMenuLinks = activeItemMegaMenu
    ? activeItemMegaMenu.megaMenuLinks || []
    : null

  const renderedBrands = React.useMemo(() => {
    const brands = deviceTypeData.brands || []
    const brandsToFeature = deviceTypeConfig.featuredBrands || []
    const featuredBrands = brandsToFeature.map(({ brand, devicesToShow }) => {
      const brandData =
        brands.find(({ brandSlug }) => brandSlug === brand) || {}
      return {
        brandName: brandData.brandName,
        brandSlug: brandData.brandSlug,
        devices: brandData.devices,
        devicesToShow,
      }
    })

    const featuredBrandSlugs = featuredBrands.map(({ brandSlug }) => brandSlug)
    const otherBrands = brands.filter(({ brandSlug }) => {
      return featuredBrandSlugs.indexOf(brandSlug) === -1
    })
    const [orderedBrands, rest] = R.partition(
      ({ brandSlug }: any) =>
        deviceTypeConfig.otherBrands.indexOf(brandSlug) !== -1,
      otherBrands
    )

    const sortedBrands = [
      ...R.sort((a: any, b: any) => {
        const indexA = R.findIndex((slug) => slug === a.brandSlug)(
          deviceTypeConfig.otherBrands
        )
        const indexB = R.findIndex((slug) => slug === b.brandSlug)(
          deviceTypeConfig.otherBrands
        )
        if (indexA === -1 && indexB !== -1) {
          return 1
        } else if (indexA !== -1 && indexB === -1) {
          return -1
        } else {
          return indexA - indexB
        }
      }, orderedBrands),
      ..._.sortBy(rest, "brandSlug"),
    ]

    return {
      brands: sortedBrands,
      featuredBrands,
    }
  }, [deviceTypeData, deviceTypeConfig])

  const renderedSeries = React.useMemo(() => {
    if (!deviceTypeConfig.showSeries) {
      return null
    }

    const allSeries = getIn(deviceTypeData, "series", [])
    const seriesToFeature = getIn(deviceTypeConfig, "series", [])
    const sortedSeries = R.sort((a: any, b: any) => {
      const indexA = R.findIndex((slug) => slug === a.slug)(seriesToFeature)
      const indexB = R.findIndex((slug) => slug === b.slug)(seriesToFeature)
      if (indexA === -1 && indexB !== -1) {
        return 1
      } else if (indexA !== -1 && indexB === -1) {
        return -1
      } else {
        return indexA - indexB
      }
    }, allSeries)

    const [featuredSeries, series] = R.partition(
      ({ slug }: any) => seriesToFeature.indexOf(slug) !== -1,
      sortedSeries
    )

    return {
      featuredSeries,
      series: _.sortBy(series, "name"),
    }
  }, [deviceTypeData, deviceTypeConfig])

  const showActiveItemMegaMenuLinks =
    activeItemMegaMenuLinks && activeItemMegaMenuLinks.length > 0

  return (
    <VerticalExpandAnimation show={!!activeItem}>
      <Container topOffset={topOffset}>
        <LeftSideMenu>
          {showActiveItemMegaMenuLinks &&
            activeItemMegaMenuLinks.map(
              ({ image, link, text }, idx: number) => (
                <VisualLink
                  image={image.file.url}
                  key={`${image} - ${idx}`}
                  link={link}
                  title={text}
                />
              )
            )}
        </LeftSideMenu>
        <Content leftOffset={activeItemMegaMenuLinks.length > 0}>
          {renderedBrands.featuredBrands.map((brand: any, i: number) => (
            <Column
              col={deviceTypeConfig.columns}
              key={`${i}-${activeItem}-${brand.brandSlug}`}
            >
              <Brand>
                <DeviceList
                  deviceTypeSlug={activeItem}
                  key={`${activeItem}-${brand.brandSlug}`}
                  {...brand}
                />
              </Brand>
            </Column>
          ))}
          {activeItem &&
            deviceTypeConfig.showOtherBrands &&
            renderedBrands?.brands && (
              <Column col={deviceTypeConfig.columns}>
                <BrandList
                  brands={renderedBrands.brands}
                  deviceType={activeItem}
                  header={deviceTypeConfig.otherBrandsLabel}
                />
              </Column>
            )}
          {activeItem && deviceTypeConfig.showSeries && renderedSeries && (
            <Column col={deviceTypeConfig.columns}>
              <SeriesList allSeries={renderedSeries} deviceType={activeItem} />
            </Column>
          )}
        </Content>
        <RightSideMenu>
          <CloseButton onClick={onRequestClose}>
            <Close strokeColor={colors.mediumDarkGray} strokeWidth="2.5" />
          </CloseButton>
        </RightSideMenu>
      </Container>
    </VerticalExpandAnimation>
  )
}

export default MegaMenu
